import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { useMemo } from 'react';
import { useActivePlan } from './useActivePlan';
import { UserOutSchema } from '@app/auth/api/auth.schema';

export enum EOnboardingStage {
  NONE = 'NONE',
  NO_QR = 'NO_QR',
  QR_NO_SCAN = 'QR_NO_SCAN',
  COMPLETE = 'COMPLETE',
}

export enum ETrialPhase {
  NONE = 'NONE',
  ACTIVATION_FOCUS = 'ACTIVATION_FOCUS',
  URGENCY_BEGINS = 'URGENCY_BEGINS',
  FINAL_PUSH = 'FINAL_PUSH',
  EXPIRED = 'EXPIRED',
}

export enum EOnboardingModalType {
  NONE = 'NONE',
  MODAL_2A = 'MODAL_2A',
  MODAL_2B = 'MODAL_2B',
  SCAN_CONGRATULATION = 'SCAN_CONGRATULATION',
  MODAL_HIGH_INTENT_USER = 'MODAL_HIGH_INTENT_USER',
  MODAL_TRIAL_EXPIRED = 'MODAL_TRIAL_EXPIRED',
}

const getOnboardingStage = (currentUser: UserOutSchema | null): EOnboardingStage => {
  if (!currentUser) {
    return EOnboardingStage.NONE;
  }
  if (currentUser.totalCodes === 0 && !currentUser.totalScans) {
    return EOnboardingStage.NO_QR;
  }
  if (currentUser.totalScans === 0) {
    return EOnboardingStage.QR_NO_SCAN;
  }
  return EOnboardingStage.COMPLETE;
};

const getTrialPhase = (isTrial: boolean, daysToExpire?: number): ETrialPhase => {
  if (!isTrial || daysToExpire === undefined) {
    return ETrialPhase.NONE;
  }
  if (daysToExpire < 0) {
    return ETrialPhase.EXPIRED;
  }
  if (daysToExpire < 3) {
    return ETrialPhase.FINAL_PUSH;
  }
  if (daysToExpire < 6) {
    return ETrialPhase.URGENCY_BEGINS;
  }
  return ETrialPhase.ACTIVATION_FOCUS;
};

const getOnboardingModalType = ({
  onboardingStageStep,
  currentTrialPhase,
  // totalCodes = 0,
  totalScans = 0,
  isTrialExtended = false,
  isFirstScanSeen = false,
}: {
  onboardingStageStep: EOnboardingStage;
  currentTrialPhase: ETrialPhase;
  totalCodes?: number;
  totalScans?: number;
  isTrialExtended?: boolean;
  isFirstScanSeen?: boolean;
}): EOnboardingModalType => {
  if (currentTrialPhase === ETrialPhase.EXPIRED) {
    return EOnboardingModalType.MODAL_TRIAL_EXPIRED;
  }
  if (currentTrialPhase === ETrialPhase.ACTIVATION_FOCUS) {
    if (onboardingStageStep === EOnboardingStage.NO_QR) {
      return EOnboardingModalType.MODAL_2A;
    }
    if (onboardingStageStep === EOnboardingStage.QR_NO_SCAN) {
      return EOnboardingModalType.MODAL_2B;
    }

    // TODO: Leave this commented for now
    // if (onboardingStageStep === EOnboardingStage.COMPLETE) {
    //   if (totalCodes >= 3) {
    //     return EOnboardingModalType.MODAL_HIGH_INTENT_USER;
    //   }
    // }
  }
  if (totalScans > 0 && !isTrialExtended && !isFirstScanSeen) {
    return EOnboardingModalType.SCAN_CONGRATULATION;
  }

  return EOnboardingModalType.NONE;
};

export const useTrialPhasesConfig = () => {
  const { currentUser } = useAuthorizedUser();
  const { isTrial = false, daysToExpire, isTrialExtended } = useActivePlan();

  const onboardingStageStep = useMemo(() => getOnboardingStage(currentUser), [currentUser]);

  const currentTrialPhase = useMemo(() => getTrialPhase(isTrial, daysToExpire), [isTrial, daysToExpire]);

  const modalType = useMemo(
    () =>
      getOnboardingModalType({
        onboardingStageStep,
        currentTrialPhase,
        totalCodes: currentUser?.totalCodes,
        totalScans: currentUser?.totalScans,
        isFirstScanSeen: currentUser?.isFirstScanSeen,
        isTrialExtended,
      }),
    [
      onboardingStageStep,
      currentTrialPhase,
      currentUser?.totalCodes,
      currentUser?.totalScans,
      currentUser?.isFirstScanSeen,
      isTrialExtended,
    ]
  );

  return {
    onboardingStageStep,
    currentTrialPhase,
    modalType,
  };
};
